<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Page Header -->
            <v-card-title class="page-header py-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Doctor</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Update basic personal and contact information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.push('/doctors')"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Doctors
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Loading State -->
            <section
              v-if="isLoadingData"
              style="height: 400px"
              class="d-flex align-center justify-center"
            >
              <div class="text-center">
                <health-loader message="Loading doctor data..." />
              </div>
            </section>

            <!-- Form Content -->
            <v-card-text v-else class="px-4 py-3">
              <v-form ref="basicForm" v-model="basicFormValid" lazy-validation>
                      <div class="form-section">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2"
                            >mdi-account</v-icon
                          >
                          Basic Information
                        </h3>

                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="basicForm.first_name"
                              label="First Name *"
                              placeholder="Enter first name"
                              outlined
                              :rules="[
                                (v) => !!v || 'First name is required',
                                (v) =>
                                  (v && v.length >= 2) ||
                                  'First name must be at least 2 characters',
                                (v) =>
                                  (v && v.length <= 50) ||
                                  'First name must be less than 50 characters',
                                (v) =>
                                  (v && /^[a-zA-Z\s]+$/.test(v)) ||
                                  'First name can only contain letters and spaces',
                              ]"
                              :error-messages="errors.first_name"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="basicForm.middle_name"
                              label="Middle Name (Optional)"
                              placeholder="Enter middle name"
                              outlined
                              :rules="[
                                (v) =>
                                  !v ||
                                  v.length <= 50 ||
                                  'Middle name must be less than 50 characters',
                                (v) =>
                                  !v ||
                                  /^[a-zA-Z\s]+$/.test(v) ||
                                  'Middle name can only contain letters and spaces',
                              ]"
                              :error-messages="errors.middle_name"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="basicForm.last_name"
                              label="Last Name *"
                              placeholder="Enter last name"
                              outlined
                              :rules="[
                                (v) => !!v || 'Last name is required',
                                (v) =>
                                  (v && v.length >= 2) ||
                                  'Last name must be at least 2 characters',
                                (v) =>
                                  (v && v.length <= 50) ||
                                  'Last name must be less than 50 characters',
                                (v) =>
                                  (v && /^[a-zA-Z\s]+$/.test(v)) ||
                                  'Last name can only contain letters and spaces',
                              ]"
                              :error-messages="errors.last_name"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="basicForm.email"
                              label="Email Address *"
                              placeholder="Enter email address"
                              outlined
                              type="email"
                              :rules="[
                                (v) => !!v || 'Email is required',
                                (v) =>
                                  /.+@.+\..+/.test(v) || 'Email must be valid',
                                (v) =>
                                  (v && v.length <= 100) ||
                                  'Email must be less than 100 characters',
                              ]"
                              :error-messages="errors.email"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-email</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="basicForm.phone"
                              label="Phone Number *"
                              placeholder="Enter phone number"
                              outlined
                              :rules="[
                                (v) => !!v || 'Phone number is required',
                                (v) => {
                                  if (!v) return 'Phone number is required';
                                  const phoneStr = String(v).replace(/[\s()-]/g, '');
                                  return /^[+]?[0-9]{10,}$/.test(phoneStr) || 'Phone number must be at least 10 digits';
                                },
                              ]"
                              :error-messages="errors.phone"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-phone</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="basicForm.gender_id"
                              :items="genderItems"
                              label="Gender (Optional)"
                              placeholder="Select gender"
                              outlined
                              :error-messages="errors.gender_id"
                              class="professional-field"
                              hide-details="auto"
                              clearable
                            >
                              <template #prepend-inner>
                                <v-icon color="grey"
                                  >mdi-human-male-female</v-icon
                                >
                              </template>
                            </v-select>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-menu
                              v-model="dobPickerMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              max-width="290px"
                              min-width="auto"
                            >
                              <template #activator="{ on, attrs }">
                                <v-text-field
                                  :value="formattedDOB"
                                  label="Date of Birth (Optional)"
                                  prepend-inner-icon="mdi-calendar"
                                  outlined
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                  :error-messages="errors.dob"
                                  class="professional-field"
                                  hide-details="auto"
                                  clearable
                                  @click:clear="clearDOB"
                                  placeholder="Select date of birth"
                                />
                              </template>
                              <v-date-picker
                                v-model="basicForm.dob"
                                no-title
                                scrollable
                                color="primary"
                                :max="maxDate"
                                :min="minDate"
                                @input="dobPickerMenu = false"
                              />
                            </v-menu>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-textarea
                              v-model="basicForm.address"
                              label="Address (Optional)"
                              placeholder="Enter complete address"
                              outlined
                              rows="3"
                              :error-messages="errors.address"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-map-marker</v-icon>
                              </template>
                            </v-textarea>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-textarea
                              v-model="basicForm.bio"
                              label="About Doctor (Optional)"
                              placeholder="Enter a short bio for this doctor"
                              outlined
                              rows="3"
                              :error-messages="errors.bio"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-text-account</v-icon>
                              </template>
                            </v-textarea>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-switch
                              v-model="basicForm.is_active"
                              label="Active Status"
                              color="success"
                              :true-value="1"
                              :false-value="0"
                              class="mt-0"
                            >
                              <template #label>
                                <span class="text-body-2">
                                  Status:
                                  <strong
                                    :class="
                                      basicForm.is_active
                                        ? 'success--text'
                                        : 'error--text'
                                    "
                                  >
                                    {{
                                      basicForm.is_active ? "Active" : "Inactive"
                                    }}
                                  </strong>
                                </span>
                              </template>
                            </v-switch>
                          </v-col>
                        </v-row>
                      </div>

                      <!-- Bank Details Section -->
                      <div class="form-section mt-4">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                          Bank Details
                        </h3>
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.account_name"
                              label="Account Holder Name"
                              outlined
                              dense
                              clearable
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.account_number"
                              label="Account Number"
                              outlined
                              dense
                              clearable
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.bank_name"
                              label="Bank Name"
                              outlined
                              dense
                              clearable
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.branch"
                              label="Branch"
                              outlined
                              dense
                              clearable
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.ifsc_code"
                              label="IFSC Code"
                              outlined
                              dense
                              clearable
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </div>
                </v-form>
              </v-card-text>

            <v-divider></v-divider>

            <!-- Form Actions -->
            <v-card-actions v-if="!isLoadingData" class="form-actions py-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Form Status -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      :color="basicFormValid ? 'success' : 'warning'"
                      outlined
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>{{
                        basicFormValid
                          ? "mdi-check-circle"
                          : "mdi-alert-circle"
                      }}</v-icon>
                      {{ basicFormValid ? "Form Valid" : "Form Invalid" }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{
                        basicFormValid
                          ? "Ready to update"
                          : "Please complete required fields"
                      }}
                    </span>
                  </div>
                </v-col>

                <!-- Right Side: Action Buttons -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      outlined
                      color="grey darken-1"
                      @click="$router.push('/doctors')"
                      class="mr-2"
                      :disabled="loading"
                    >
                      <v-icon left>mdi-close</v-icon>
                      Cancel
                    </v-btn>
                    <v-btn
                      color="success"
                      @click="updateDoctor"
                      :loading="loading"
                      :disabled="loading || !basicFormValid"
                      class="submit-button"
                    >
                      <v-icon left>mdi-check</v-icon>
                      Update Doctor
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "DoctorEditPage",
  data() {
    return {
      loading: false,
      isLoadingData: true,
      validationErrors: {},

      // Date picker
      dobPickerMenu: false,

      // Form validation states
      basicFormValid: false,

      // Step 1: Basic Details
      basicForm: {
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender_id: null,
        dob: "",
        address: "",
        bio: "",
        is_active: 1,
      },

      // Bank Details
      bankForm: {
        account_name: "",
        account_number: "",
        bank_name: "",
        branch: "",
        ifsc_code: "",
      },

      // Data lists
      genderItems: [],
    };
  },

  computed: {
    doctorId() {
      return this.$route.params.id;
    },

    formattedDOB() {
      if (!this.basicForm.dob) return "";

      const date = new Date(this.basicForm.dob);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    maxDate() {
      const today = new Date();
      today.setFullYear(today.getFullYear() - 18);
      return today.toISOString().substr(0, 10);
    },

    minDate() {
      const today = new Date();
      today.setFullYear(today.getFullYear() - 100);
      return today.toISOString().substr(0, 10);
    },

    errors() {
      return this.validationErrors || {};
    },
  },

  mounted() {
    if (!this.doctorId) {
      this.$toast.error("Doctor ID not found");
      this.$router.push("/doctors");
      return;
    }
    this.getMasters();
    this.loadDoctorData();
  },

  methods: {
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=doctor_specialities`);
        const masters = response.data;

        // Gender options
        if (masters.genders) {
          this.genderItems = masters.genders.map(gender => ({
            text: gender.description,
            value: gender.id
          }));
        }
      } catch (error) {
        console.log("Error loading masters:", error);
      }
    },

    async loadDoctorData() {
      this.isLoadingData = true;
      try {
        // Load user basic details
        const userResponse = await axios.get(`users/${this.doctorId}`);
        const userData = userResponse.data.data || userResponse.data;

        // Populate basic form
        this.basicForm = {
          first_name: userData.first_name || "",
          middle_name: userData.middle_name || "",
          last_name: userData.last_name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          gender_id: userData.gender_id || null,
          dob: userData.dob || "",
          address: userData.address || "",
          bio: userData.bio || "",
          is_active: userData.is_active ? 1 : 0,
        };

        // Load bank details
        try {
          const bankResponse = await axios.get(`bank_details?user_id=${this.doctorId}`);
          const bankData = bankResponse.data.data?.[0];
          if (bankData) {
            this.bankForm = {
              account_name: bankData.account_name || "",
              account_number: bankData.account_number || "",
              bank_name: bankData.bank_name || "",
              branch: bankData.branch || "",
              ifsc_code: bankData.ifsc_code || "",
            };
          }
        } catch (e) {
          // Bank details not required — silently ignore
        }

        this.$toast.success("Doctor data loaded successfully");
      } catch (error) {
        console.error("Error loading doctor data:", error);
        this.$toast.error("Failed to load doctor data");
        this.$router.push("/doctors");
      } finally {
        this.isLoadingData = false;
      }
    },

    async updateDoctor() {
      // Validate form
      if (!this.$refs.basicForm || !this.$refs.basicForm.validate()) {
        this.$toast.error("Please complete all required fields.");
        return;
      }

      this.loading = true;
      this.validationErrors = {};

      try {
        // Use PATCH method for update — include bank fields prefixed with bank_
        await axios.patch(`users/${this.doctorId}`, {
          ...this.basicForm,
          bank_account_name:   this.bankForm.account_name,
          bank_account_number: this.bankForm.account_number,
          bank_bank_name:      this.bankForm.bank_name,
          bank_branch:         this.bankForm.branch,
          bank_ifsc_code:      this.bankForm.ifsc_code,
        });

        this.$toast.success("Doctor updated successfully!");
        this.$router.push("/doctors");
      } catch (error) {
        console.error("Error updating doctor:", error);
        if (error.response && error.response.status === 422) {
          this.validationErrors = error.response.data.errors || {};
          this.$toast.error("Please check the form for validation errors.");
        } else {
          this.$toast.error("An error occurred. Please try again.");
        }
      } finally {
        this.loading = false;
      }
    },

    clearDOB() {
      this.basicForm.dob = "";
      this.dobPickerMenu = false;
    },
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.page-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 1px solid #e0e0e0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.professional-field >>> .v-input__control {
  border-radius: 8px !important;
}

.professional-field >>> .v-text-field__details {
  margin-top: 8px;
}

.form-actions {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-top: 1px solid #e0e0e0;
}

.submit-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 24px;
}

.v-chip {
  font-weight: 500;
}
</style>
